// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import QtyField from "@steiner/App/Components/Qty/QtyField";
import xLoadingOverlay from "@steiner/App/Handlers/XNG/LoadingOverlay";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import Button from "@steiner/MobiusOverrides/Button";
import TextArea, { TextAreaPresentationProps } from "@steiner/MobiusOverrides/TextArea/TextArea";
import TextField, { TextFieldPresentationProps } from "@steiner/MobiusOverrides/TextField";
import { xValidateOnSubmit } from "@steiner/XDL/Utilities";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
        customSharedSettings: { nonStockProductId },
    } = getSettingsCollection(state);

    return {
        nonStockProductId,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
    };
};

const mapDispatchToProps = {
    addToCart,
    xLoadingOverlay,
};

export interface NonStockedItemsStyles {
    container?: InjectableCss;
    headerWrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    contentTitle?: TypographyPresentationProps;
    contentDescription?: TypographyPresentationProps;
    partNumberTextField?: TextFieldPresentationProps;
    descriptionTextArea?: TextAreaPresentationProps;
    actionsWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
}

export const nonStockedItemsStyles: NonStockedItemsStyles = {};

const styles = nonStockedItemsStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const formId = "AddNonStockedItemsForm";

const NonStockedItems: FC<Props> = ({
    id,
    nonStockProductId,
    addToCart,
    xLoadingOverlay,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
}) => {
    const toasterContext = useContext(ToasterContext);

    const [partNumber, setPartNumber] = useState("");
    const [description, setDescription] = useState("");
    const [qtyNumber, setQtyNumber] = useState(1);

    const partNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPartNumber(event.currentTarget.value);
    };

    const descriptionChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(event.target.value);
    };

    const clearForm = () => {
        setPartNumber("");
        setDescription("");
        setQtyNumber(1);
    };

    const handleSubmitForm = (e: any) => {
        e.preventDefault();
        const errors = xValidateOnSubmit(formId);
        if (errors.length > 0) {
            return;
        }
        xLoadingOverlay({ key: "add", id });
        addToCart({
            productId: nonStockProductId,
            qtyOrdered: qtyNumber,
            unitOfMeasure: "",
            properties: {
                NonStockPartNo: partNumber,
                NonStockDescription: description,
                IsWidgetNonStockItem: "true",
            },
            onSuccess: (cartLine: CartLineModel) => {
                clearForm();
                xLoadingOverlay({ key: "clear", id });
                if (showAddToCartConfirmationDialog) {
                    toasterContext.addToast({
                        body: (
                            <ProductAddedToCartMessage
                                isQtyAdjusted={cartLine.isQtyAdjusted}
                                multipleProducts={false}
                            />
                        ),
                        messageType: "success",
                        timeoutLength: cartLine.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
                    });
                }
            },
            onError: () => {
                xLoadingOverlay({ key: "clear", id });
            },
        } as any);
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="cart-page-add-nonStockedItems-container">
            <StyledWrapper {...styles.headerWrapper}>
                <Typography {...styles.headerText}>{translate("Add Nonstocked Items")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.contentWrapper}>
                <Typography {...styles.contentTitle}>{translate("Can't Find What You Need?")}</Typography>
                <Typography {...styles.contentDescription}>{siteMessage("STR_Non_Stock_Wiget_Msg")}</Typography>
                <form onSubmit={handleSubmitForm}>
                    <TextField
                        {...styles.partNumberTextField}
                        placeholder={translate("i.e. abc-123-xyz")}
                        label={translate("Part #")}
                        value={partNumber}
                        onChange={partNumberChangeHandler}
                        required
                        {...{ formId }}
                    />
                    <TextArea
                        {...styles.descriptionTextArea}
                        placeholder={translate("Add your product's description")}
                        label={translate("Description")}
                        value={description}
                        onChange={descriptionChangeHandler}
                        type="description"
                        required
                        {...{ formId }}
                    />
                    <StyledWrapper {...styles.actionsWrapper}>
                        <QtyField
                            value={qtyNumber}
                            data-test-selector="product_qtyOrdered"
                            onChange={e => {
                                setQtyNumber(Number(e.currentTarget.value));
                            }}
                        />
                        <Button disabled={!qtyNumber || !description} {...styles.addToCartButton}>
                            {translate("Add to Cart")}
                        </Button>
                    </StyledWrapper>
                </form>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NonStockedItems),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
